/**
 * The dates the activity views work in.
 *
 * The feeds take plain days, `YYYY-MM-DD`, inclusive at both ends. The range a
 * view opens with is the last thirty days, so the first request is bounded and
 * the person can see in the two date boxes why older entries are not there.
 */

/** A day as the feeds take it, in the reader's own calendar. */
export function toDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Today and the thirty days before it. */
export function lastThirtyDays(now: Date = new Date()): { from: string; to: string } {
  const from = new Date(now);
  from.setDate(from.getDate() - 30);
  return { from: toDay(from), to: toDay(now) };
}

/** When an entry happened: the day and the time, as the reader writes them. */
export function formatMoment(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/** How long ago, in words: "3 hours ago", "yesterday". */
export function relativeTime(value: string, now: Date = new Date()): string {
  const seconds = Math.round((new Date(value).getTime() - now.getTime()) / 1000);
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  for (const [unit, size] of steps) {
    if (Math.abs(seconds) >= size)
      return RELATIVE.format(Math.round(seconds / size), unit);
  }
  return RELATIVE.format(seconds, 'second');
}
